import { Job, QuantityChange, SaveVisitPayload, Timesheet, Visit } from "./editVisitApi"
import * as LaborModel from './labor'
import * as ItemModel from './item'
import * as StatusModel from './status'
import dayjs from 'dayjs'
import { CatalogItem } from '../../domain/catalogItem'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { ScheduledEvent } from "../ScheduleManagement/scheduleApi"
import { ProjectStatus, VisitWorkareaStatus } from "../../common/constants"

dayjs.extend(utc)
dayjs.extend(timezone)

export type Dispatch = (action: Action) => void

export type EditVisitData = {
  isNewVisit: boolean
  visitId: string
  jobId?: number
  laborChanges: LaborModel.LaborChanges
  itemChanges: ItemModel.WorkareaItemChanges[]
  statusChanges: StatusModel.StatusChange[]
  jobStatusChanges: StatusModel.JobStatusChange[]
  internalNotes?: string
  clientNotes?: string
  visitDate?: string
  sendNotification?: boolean
}

const createTimestampAtHour = (hour: number, timeZone: string, date?: string): string => {
  return dayjs(date ?? new Date())
    .tz(timeZone)
    .hour(hour)
    .startOf('hour')
    .toISOString()
}

export const createEditVisitDataFromVisitId = (visitId: string, timeZone: string, event?: ScheduledEvent, jobId?: number): EditVisitData => {
  const isNewVisit = !visitId
  const startAt = createTimestampAtHour(8, timeZone)
  const endAt = createTimestampAtHour(17, timeZone)

  return {
    isNewVisit,
    visitId,
    jobId: event?.jobId ?? jobId,
    laborChanges: LaborModel.newLaborChanges(startAt, endAt),
    itemChanges: [],
    statusChanges: [],
    jobStatusChanges: [],
    visitDate: isNewVisit ? startAt : undefined
  }
}

export type Action =
  | { type: 'VISIT_ID_CHANGED', payload: string, timeZone: string, jobId?: number }
  | { type: 'JOB_ID_CHANGED', payload: number }
  | { type: 'WORKAREA_ID_CHANGED', timesheet: Timesheet, payload?: number }
  | { type: 'STARTED_AT_CHANGED', timesheet: Timesheet, payload: string }
  | { type: 'ENDED_AT_CHANGED', timesheet: Timesheet, payload?: string }
  | { type: 'LUNCH_CHANGED', timesheet: Timesheet, payload?: number }
  | { type: 'ADD_TIMESHEET', timesheet: Timesheet }
  | { type: 'REMOVE_TIMESHEET', timesheet: Timesheet }
  | { type: 'TIMESHEET_RESTORED', timesheet: Timesheet }
  | { type: 'SET_BULK_START_AT', payload: string }
  | { type: 'SET_BULK_END_AT', payload?: string }
  | { type: 'SET_BULK_LUNCH', payload?: number }
  | { type: 'SET_BULK_WORKAREA_ID', payload?: number }
  | { type: 'SET_SELECTED_CREW_MEMBERS', payload: number[] }
  | { type: 'ADD_CREW_MEMBERS' }
  | { type: 'ADD_ITEM', workareaId: number, item: CatalogItem }
  | { type: 'CHANGE_ITEM_QUANTITY', workareaId: number, quantity: QuantityChange }
  | { type: 'REMOVE_ITEM', workareaId: number, catalogItemGuid: string }
  | { type: 'INTERNAL_NOTES_CHANGED', payload: string | undefined }
  | { type: 'CLIENT_NOTES_CHANGED', payload: string | undefined }
  | { type: 'VISIT_DATE_CHANGED', payload: string }
  | { type: 'BULK_DATE_CHANGED', payload: string }
  | { type: 'WORKAREA_STATUS_CHANGED', payload: VisitWorkareaStatus, workareaId: number }
  | { type: 'JOB_WORKAREA_STATUS_CHANGED', payload: ProjectStatus, workareaId: number }
  | { type: 'SEND_NOTIFICATION_CHANGED', payload: boolean }

export const reducer = (state: EditVisitData, action: Action): EditVisitData => {
  switch (action.type) {
    case 'VISIT_ID_CHANGED':
      return createEditVisitDataFromVisitId(action.payload, action.timeZone, undefined, action.jobId)
    case 'JOB_ID_CHANGED':
      return { ...state, jobId: action.payload, laborChanges: LaborModel.clearWorkareaIds(state.laborChanges), itemChanges: [] }
    case 'WORKAREA_ID_CHANGED':
      return { ...state, laborChanges: LaborModel.updateWorkarea(state.laborChanges, action.timesheet, action.payload) }
    case 'STARTED_AT_CHANGED':
      return { ...state, laborChanges: LaborModel.updateStartedAt(state.laborChanges, action.timesheet, action.payload) }
    case 'ENDED_AT_CHANGED':
      return { ...state, laborChanges: LaborModel.updateEndedAt(state.laborChanges, action.timesheet, action.payload) }
    case 'LUNCH_CHANGED':
      return { ...state, laborChanges: LaborModel.updateLunch(state.laborChanges, action.timesheet, action.payload) }
    case 'ADD_TIMESHEET':
      return { ...state, laborChanges: LaborModel.newTimesheetForCrewMember(state.laborChanges, action.timesheet) }
    case 'REMOVE_TIMESHEET':
      return { ...state, laborChanges: LaborModel.removeTimesheet(state.laborChanges, action.timesheet) }
    case 'TIMESHEET_RESTORED':
      return { ...state, laborChanges: LaborModel.restoreTimesheet(state.laborChanges, action.timesheet) }
    case 'SET_BULK_START_AT':
      return { ...state, laborChanges: LaborModel.setBulkStartAt(state.laborChanges, action.payload) }
    case 'SET_BULK_END_AT':
      return { ...state, laborChanges: LaborModel.setBulkEndAt(state.laborChanges, action.payload) }
    case 'SET_BULK_LUNCH':
      return { ...state, laborChanges: LaborModel.setBulkLunch(state.laborChanges, action.payload) }
    case 'SET_BULK_WORKAREA_ID':
      return { ...state, laborChanges: LaborModel.setBulkWorkareaId(state.laborChanges, action.payload) }
    case 'SET_SELECTED_CREW_MEMBERS':
      return { ...state, laborChanges: LaborModel.setSelectedCrewMembers(state.laborChanges, action.payload) }
    case 'ADD_CREW_MEMBERS':
      return { ...state, laborChanges: LaborModel.addCrewMembers(state.laborChanges) }
    case 'ADD_ITEM':
      return { ...state, itemChanges: ItemModel.addItem(state.itemChanges, action.workareaId, action.item) }
    case 'CHANGE_ITEM_QUANTITY':
      return { ...state, itemChanges: ItemModel.changeQuantity(state.itemChanges, action.workareaId, action.quantity) }
    case 'REMOVE_ITEM':
      return { ...state, itemChanges: ItemModel.removeItem(state.itemChanges, action.workareaId, action.catalogItemGuid) }
    case 'INTERNAL_NOTES_CHANGED':
      return { ...state, internalNotes: action.payload }
    case 'CLIENT_NOTES_CHANGED':
      return { ...state, clientNotes: action.payload }
    case 'VISIT_DATE_CHANGED':
      return { ...state, visitDate: action.payload, laborChanges: LaborModel.updateBulkDate(state.laborChanges, action.payload) }
    case 'BULK_DATE_CHANGED':
      return { ...state, laborChanges: LaborModel.updateBulkDate(state.laborChanges, action.payload) }
    case 'WORKAREA_STATUS_CHANGED':
      return { ...state, statusChanges: StatusModel.updateStatus(state.statusChanges, { status: action.payload, workareaId: action.workareaId }) }
    case 'JOB_WORKAREA_STATUS_CHANGED':
      return { ...state, jobStatusChanges: StatusModel.updateJobStatus(state.jobStatusChanges, { status: action.payload, workareaId: action.workareaId }) }
    case 'SEND_NOTIFICATION_CHANGED':
      return { ...state, sendNotification: action.payload }
    default:
      return state
  }
}

export const getJobId = (data: EditVisitData, visit?: Visit): number | undefined => {
  if (data.jobId !== undefined) {
    return data.jobId
  }
  if (!data.isNewVisit && visit) {
    return visit.jobId
  }
}

export const isVisitDateChanged = (serverDate: string | undefined, state: EditVisitData) => {
  return !state.isNewVisit &&
    state.visitDate !== undefined &&
    dayjs(state.visitDate).startOf('day').toISOString() !== dayjs(serverDate).startOf('day').toISOString()
}

export const mapStateToRequest = (state: EditVisitData, visit: Visit, submitTimesheets: boolean, job: Job, _workareaIds: number[]): SaveVisitPayload => {
  return {
    isNewVisit: state.isNewVisit,
    visitId: state.isNewVisit ? null : state.visitId,
    jobId: state.jobId ?? visit.jobId,
    itemChanges: state.itemChanges.map(x => ({
      workareaId: x.workareaId,
      items: x.quantityChanges
    })),
    timesheetChanges: {
      added: state.laborChanges.addedLabor.map(x => ({ ...x, gpsLog: [] })),
      changed: state.laborChanges.changedLabor.map(x => ({ ...x, gpsLog: [] })),
      removedTimesheetIds: state.laborChanges.removedLaborIds
    },
    workareaStatuses: state.statusChanges.filter(x => job.workareas.some(y => y.id === x.workareaId)),
    jobWorkareaStatuses: state.jobStatusChanges.filter(x => job.workareas.some(y => y.id === x.workareaId)),
    submitTimesheets,
    clientNotes: state.clientNotes,
    internalNotes: state.internalNotes,
    visitDate: state.visitDate,
    sendNotification: state.sendNotification ?? false
  }
}

export const shouldWarnBeforeDiscardingChanges = (state: EditVisitData) =>
  state.laborChanges.changedLabor.length > 0
  || state.laborChanges.addedLabor.length > 0
  || state.laborChanges.removedLaborIds.length > 0
  || state.itemChanges.length > 0
  || state.statusChanges.length > 0
  || state.jobStatusChanges.length > 0
  || state.internalNotes !== undefined
  || state.clientNotes !== undefined
  || (state.visitDate !== undefined && !state.isNewVisit)

